/**
 * VesselFinder credit-wallet primitives — the TOI/balance rules shared by
 * every priced control on this operator. Mirrors
 * `ais/voyage-radar/voyage-credits.model.ts`; read that file's header for the
 * reasoning behind the null-vs-zero and `||`-vs-`??` choices repeated here.
 */

import { actionCredits, canAfford as catalogueCanAfford } from 'src/app/services/operators/vesselfinder-credit-actions';

/**
 * Credits left on a sensor, or NULL when the wallet is unknown (never 0 for
 * "unknown" — see voyage-credits.model.ts's header for why that distinction
 * matters).
 */
export function vesselFinderCreditsRemaining(sensor: any): number | null {
  if (!sensor) return null;
  // eslint-disable-next-line eqeqeq
  const raw = sensor.apiCreditsRemaining == 0 ? 0 : sensor.apiCreditsRemaining || sensor.apiCredits;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

/** Reconcile the sensor's local wallet with a successful VesselFinder response. */
export function applyVesselFinderCreditSpend(
  sensor: any,
  response: any,
  cost: number,
): void {
  if (!sensor) return;

  const reported = response?.apiCreditsRemaining;
  const authoritative = Number(reported);
  if (reported != null && Number.isFinite(authoritative)) {
    sensor.apiCreditsRemaining = Math.max(0, authoritative);
    return;
  }

  const current = vesselFinderCreditsRemaining(sensor);
  const base = current != null ? current : Number(sensor.apiCredits);
  if (Number.isFinite(base)) {
    sensor.apiCreditsRemaining = Math.max(0, base - cost);
  }
}

/**
 * The scene this sensor's TOI answers should be read from — the ticked one, or
 * the first. Mirrors `voyageSensorScene`.
 *
 * VesselFinder draws nothing from scenes (global coverage, no AOI), but the
 * sensor view-model is still built by the shared scene branch in
 * `leaflet-newcollections.component.ts`, so the task's window lands on them.
 * Used only as the FALLBACK for a sensor shape that carries no window itself.
 */
function vesselFinderSensorScene(sensor: any): any {
  const scenes = sensor?.scenes ?? [];
  return scenes.find((s: any) => s?.checked) ?? scenes[0] ?? null;
}

/**
 * Has the task's time of interest ended? Same timestamp-wins-over-flags
 * resolution as `hasVoyageToiFinished` — the close-window date is the
 * authority; `timeExpired`/`aisDownloadable` are only the fallback for a
 * shape with no window at all.
 */
export function hasVesselFinderToiFinished(sensor: any, nowMs: number = Date.now()): boolean {
  const closeWindow = sensor?.closeWindow;
  const closeMs = closeWindow ? new Date(closeWindow).getTime() : NaN;
  if (Number.isFinite(closeMs)) return nowMs > closeMs;

  if (sensor?.timeExpired === true) return true;
  return sensor?.aisDownloadable === true;
}

/**
 * Has the task's time of interest not OPENED yet? The other end of the same
 * window `hasVesselFinderToiFinished` closes, resolved the same way: the
 * open-window date is the authority, `scene.aisAvailable` is only the fallback
 * for a shape with no window at all.
 *
 * The sensor's own `openWindow` is read before the scene's because the scene is
 * incidental here: it belongs to the shared view-model, not to anything this
 * operator draws, and a task shape that produced none must still gate.
 *
 * Strictly `=== false` on the flag, and an ABSENT window is NOT "not started":
 * an unhydrated sensor must not read as a shut one and lock the operator out
 * of a task whose TOI is open. Same call `isVoyageToiNotStarted` makes.
 */
export function isVesselFinderToiNotStarted(sensor: any, nowMs: number = Date.now()): boolean {
  const scene = vesselFinderSensorScene(sensor);
  const openWindow = sensor?.openWindow ?? scene?.openWindow;
  const openMs = openWindow ? new Date(openWindow).getTime() : NaN;
  if (Number.isFinite(openMs)) return nowMs < openMs;

  return scene?.aisAvailable === false;
}

/**
 * Word for word the sentence `fetchAISDataFromServer` raises on the
 * Spire/Clyde/Voyage Radar path (`VOYAGE_TOI_NOT_STARTED_MESSAGE`) — one fact,
 * one sentence, whoever the operator is.
 */
export const VESSEL_FINDER_TOI_NOT_STARTED_MESSAGE =
  'AIS data is not available until the start of the time of interest (TOI)';

export const VESSEL_FINDER_TOI_ENDED_MESSAGE =
  'The time of interest (TOI) has ended, so credits can no longer be spent on ' +
  'this target. Any credits left can be refunded — use the refund icon on the ' +
  'sensor row. Please re-task if the target continues to be the same.';

/** Whether there is a refund worth surfacing — non-zero balance, no terminal/pending refund state already reached. */
export function canRequestVesselFinderRefund(sensor: any): boolean {
  const left = vesselFinderCreditsRemaining(sensor);
  return (
    left != null &&
    left > 0 &&
    !sensor?.refundRequest &&
    !sensor?.returnRefundAccept &&
    !sensor?.returnRefundReject
  );
}

/** Whether the refund request can actually be submitted — the request conditions, plus the TOI having finished. */
export function canSubmitVesselFinderRefund(sensor: any): boolean {
  return canRequestVesselFinderRefund(sensor) && hasVesselFinderToiFinished(sensor);
}

/**
 * THE pre-flight for a paid action — TOI first (a shop that has not opened, and
 * one that has closed, both have nothing to sell whatever the balance), then
 * price and balance. Same order as `voyageSensorSpendGate`/`voyageSpendRefusal`,
 * folded into one function since this operator's refusal ladder has no
 * per-action nuance to add.
 *
 * Not-started is checked before finished because the two answers come from
 * different fields and a half-hydrated sensor could otherwise satisfy both;
 * "has not started" is the one that is true of a task nobody has collected for.
 *
 * Returns a human-readable refusal, or null when the spend may proceed.
 */
export function vesselFinderSpendRefusal(sensor: any, actionKey: string): string | null {
  if (isVesselFinderToiNotStarted(sensor)) return VESSEL_FINDER_TOI_NOT_STARTED_MESSAGE;
  if (hasVesselFinderToiFinished(sensor)) return VESSEL_FINDER_TOI_ENDED_MESSAGE;
  const cost = actionCredits(sensor, actionKey);
  if (!catalogueCanAfford(sensor, cost)) {
    return `Not enough credits for this action (needs ${cost}).`;
  }
  return null;
}

/**
 * The subset of `vesselFinderSpendRefusal` a CONTROL should go dead for (e.g. empty wallet).
 * An ended TOI deliberately does NOT block the button or render in the cost line, so the control
 * stays clickable to raise the warning modal — the modal is where the refund route is explained,
 * and there is nothing to explain to someone who cannot reach it.
 *
 * An un-started TOI is the opposite case and DOES block: there is no action to
 * offer and no remedy to describe, only a date to wait for, so the button goes
 * dead with the reason in its own cost line. Same treatment Voyage Radar's
 * Refresh button gives its `not-started` state.
 */
export function vesselFinderSpendBlocker(sensor: any, actionKey: string): string | null {
  if (hasVesselFinderToiFinished(sensor)) return null;
  return vesselFinderSpendRefusal(sensor, actionKey);
}
